import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { DECK_TYPES, DECK_INFO, CATEGORIES, DIFFICULTY } from '../mock/gameData';
import { Crown, ArrowLeft } from 'lucide-react';

// Difficulty banner component for corner ribbon
const DifficultyBanner = ({ difficulty }) => {
  const getBannerConfig = () => {
    switch (difficulty) {
      case DIFFICULTY.EASY:
        return { label: 'Easy', bgColor: 'bg-green-500', textColor: 'text-white', fontSize: 'text-[10px]' };
      case DIFFICULTY.DIFFICULT:
        return { label: 'Difficult', bgColor: 'bg-yellow-500', textColor: 'text-black', fontSize: 'text-[10px]' };
      case DIFFICULTY.IMPOSSIBLE:
        return { label: 'Insanity', bgColor: 'bg-red-600', textColor: 'text-white', fontSize: 'text-[10px]' };
      default:
        return null;
    }
  };

  const config = getBannerConfig();
  if (!config) return null;

  // Corner ribbon for all difficulties
  return (
    <div className="absolute -top-1 -right-1 overflow-hidden w-20 h-20 pointer-events-none">
      <div 
        className={`${config.bgColor} ${config.textColor} ${config.fontSize} font-bold py-0.5 text-center transform rotate-45 translate-x-2 translate-y-3 w-24 shadow-md`}
      >
        {config.label}
      </div>
    </div>
  );
};

// Get category display name
const getCategoryDisplayName = (category) => {
  switch (category) {
    case CATEGORIES.THE_GLOBE: return 'The Globe';
    case CATEGORIES.LIVING_THINGS: return 'Living Things';
    case CATEGORIES.MUSIC: return 'Music';
    case CATEGORIES.MOVIES: return 'Movies';
    case CATEGORIES.ENTERTAINMENT: return 'Entertainment';
    case CATEGORIES.SPORTS: return 'Sports';
    case CATEGORIES.EPHEMERA: return 'The Abyss';
    case CATEGORIES.FOOD_AND_DRINK: return 'Food & Drink';
    case CATEGORIES.GAMING: return 'Gaming';
    case CATEGORIES.STYLE: return 'Style';
    default: return '';
  }
};

const DeckSelector = ({ onDeckSelect, onBack, gameMode = 'cards', filterCategory = null }) => {
  const deckEntries = Object.entries(DECK_INFO)
    .filter(([_, info]) => {
      if (!filterCategory) return true;
      return info.category === filterCategory;
    })
    .sort((a, b) => {
      // Sort alphabetically by deck name
      return a[1].name.localeCompare(b[1].name);
    });
    
  const totalSlotsToShow = 25;
  const emptySlotsNeeded = Math.max(0, totalSlotsToShow - deckEntries.length); 
  
  const handleRandomSelect = () => {
    const randomDeckType = deckEntries[Math.floor(Math.random() * deckEntries.length)][0];
    onDeckSelect(randomDeckType);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 flex items-center justify-center">
      <div className="max-w-8xl mx-auto text-center">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={onBack}
            size="sm"
            variant="outline"
            className="bg-white/10 border-gray-600 text-white hover:bg-white/20 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Categories
          </Button>
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center justify-center gap-2 italic font-serif">
            <Crown className="w-8 h-8" />
            Pop Culture Wars
          </h1>
          <p className="text-gray-300 text-lg">Select a deck to begin!</p>
          {filterCategory && (
            <p className="text-gray-400 text-md mt-1">Category: {getCategoryDisplayName(filterCategory)}</p>
          )}
        </div>

        {/* Random Deck Banner - Above Grid */}
        <div 
          onClick={handleRandomSelect}
          className="max-w-6xl mx-auto mb-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:from-purple-500 hover:to-pink-500 transition-all duration-300 border border-purple-400"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎲</span>
            <div className="text-left">
              <h2 className="text-white font-bold">Random Deck</h2>
              <p className="text-purple-200 text-xs">Surprise me with any {filterCategory ? getCategoryDisplayName(filterCategory) : ''} deck!</p>
            </div>
          </div>
          <Button 
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white px-4"
          >
            Shuffle!
          </Button>
        </div>

        {/* Deck Options - Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 max-w-6xl mx-auto">
          {/* Regular decks */}
          {deckEntries.map(([deckType, info]) => (
            <Card key={deckType} className={`relative bg-gradient-to-br ${info.colors.from} ${info.colors.to} backdrop-blur-sm ${info.colors.border} hover:opacity-90 transition-all duration-300 transform hover:scale-105 border-2 overflow-hidden`}>
              {/* Difficulty Banner */}
              {info.difficulty && (
                <DifficultyBanner difficulty={info.difficulty} />
              )}
              <div className="p-4 text-center">
                <div className="text-3xl mb-2">{info.icon}</div>
                <h2 className="text-sm font-bold text-white mb-3">{info.name}</h2>
                <Button 
                  onClick={() => onDeckSelect(deckType)}
                  size="sm"
                  className={`w-full ${info.colors.bg} hover:opacity-80 text-white py-1 text-xs font-bold`}
                >
                  Choose
                </Button>
              </div>
            </Card>
          ))}
          
          {/* Empty slots for future decks (only if needed to fill grid) */}
          {deckEntries.length < 5 && Array.from({ length: Math.max(0, 4 - deckEntries.length) }, (_, index) => (
             <div key={`spacer-${index}`} className="hidden md:block"></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeckSelector;
